import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Info } from 'lucide-react';
import { formatMonth, getActivityIcon, getActivityLabel } from './useContributorDashboard';

// Fallback point values match backend models.POINT_VALUES (used if backend payload is missing)
const DEFAULT_POINT_VALUES = {
  login: 1,
  forum_post: 1,
  forum_reply: 2,
  homework_review: 2,
  homework_feedback: 2,
  live_stream: 2,
  workshop_creation: 5,
};

const ACTIVITY_DESCRIPTIONS = {
  login: 'Log in each day (one award per day)',
  forum_post: 'Create a new forum post',
  forum_reply: 'Reply to a forum post',
  homework_review: 'Review a learner\'s homework submission',
  homework_feedback: 'Provide written feedback on homework',
  live_stream: 'Host a live stream',
  workshop_creation: 'Create and publish a workshop',
};

const OverviewTab = ({ points, payouts, threshold }) => {
  const progressPercent = Math.min((points?.total_points || 0) / threshold * 100, 100);
  const totalEarnings = payouts
    .filter(p => p.status === 'completed')
    .reduce((sum, p) => sum + p.payout_amount, 0);

  const pointValues = points?.point_values && Object.keys(points.point_values).length > 0
    ? points.point_values
    : DEFAULT_POINT_VALUES;
  
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Current Month Points */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-400 mb-1">This Month</p>
              <p className="text-4xl font-bold text-cyan-400">{points?.total_points || 0}</p>
              <p className="text-sm text-gray-500">points earned</p>
            </div>
          </CardContent>
        </Card>
        
        {/* Eligibility Progress */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-400 mb-2">Eligibility Progress</p>
              <Progress value={progressPercent} className="h-3 mb-2" />
              <p className="text-sm text-gray-500">
                {points?.total_points || 0} / {threshold} points
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Total Earnings */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-400 mb-1">Total Earnings</p>
              <p className="text-4xl font-bold text-green-400">
                ${totalEarnings.toFixed(2)}
              </p>
              <p className="text-sm text-gray-500">all time</p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Points Breakdown */}
      {points?.breakdown && Object.keys(points.breakdown).length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-white">Points Breakdown - {formatMonth(points.month)}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(points.breakdown).map(([type, data]) => (
                <div key={type} className="bg-slate-700/50 rounded-lg p-3 text-center">
                  <p className="text-2xl mb-1">{getActivityIcon(type)}</p>
                  <p className="text-lg font-bold text-white">{data.points} pts</p>
                  <p className="text-xs text-gray-400">{getActivityLabel(type)}</p>
                  <p className="text-xs text-gray-500">({data.count}x)</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* How to Earn Points — notice listing every rewardable activity */}
      <Card className="bg-slate-800/50 border-slate-700 mt-4" data-testid="how-to-earn-points-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-cyan-400" />
            How to Earn Points
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-300 mb-4">
            Contributors earn points by participating in the community. Reach{' '}
            <span className="font-semibold text-cyan-400">{threshold} points</span> in a month
            to become eligible for that month&apos;s revenue-share payout.
          </p>
          <ul className="divide-y divide-slate-700/60 rounded-lg overflow-hidden border border-slate-700/60" data-testid="point-rules-list">
            {Object.entries(pointValues).map(([type, pts]) => (
              <li
                key={type}
                className="flex items-center gap-3 px-3 py-2.5 bg-slate-900/40"
                data-testid={`point-rule-${type}`}
              >
                <span className="text-xl flex-shrink-0" aria-hidden="true">{getActivityIcon(type)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{getActivityLabel(type)}</p>
                  <p className="text-xs text-gray-400 truncate">
                    {ACTIVITY_DESCRIPTIONS[type] || 'Earn points for this activity'}
                  </p>
                </div>
                <span
                  className="text-sm font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-3 py-1 flex-shrink-0"
                  data-testid={`point-rule-value-${type}`}
                >
                  +{pts} pt{pts === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500 mt-3 italic">
            Note: Daily login awards are capped at one per day. Point values may be adjusted by admins from time to time.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
